import {
  FC,
  useCallback,
  useState,
  FormEvent,
  useEffect,
  useMemo,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { CloseModalButton } from "components/Buttons";
import BasicModal from "./BasicModal";
import { ModalButton, MainButton } from "components/Buttons";
import { TextInput, ReadInfo } from "components/Inputs";
import {
  ModalHeading,
  ModalContent,
  InputContainer,
  InnerContentForm,
  TwoFaQRContainer,
  QrContainer,
  QrZone,
} from "./styled";
import { buildOtpAuthUrl } from "helpers/funcs";
import { colors } from "helpers/consts";
import { AppText } from "components";
import { authStepper } from "api/auth";
import { UserStore } from "stores";
import { stepperConfig } from "api/types/auth";

type AuthStepsModalProps = {
  isStatic?: Boolean;
};

const AuthStepsModal: FC<AuthStepsModalProps> = ({ isStatic }) => {
  const { t } = useTranslation();
  const {
    authStep,
    setAuthStep,
    authSecret,
    user: { email },
  } = UserStore;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stepInput, setStepInput] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [timerCount, setTimer] = useState<number>(0);

  useEffect(() => {
    if (authStep && !isModalOpen) {
      setIsModalOpen(true);
    }
    if (!authStep && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [authStep, isModalOpen]);

  const doSubmit = useCallback(() => {
    if (!authStep) return;

    const { inputField } = stepperConfig[authStep];
    if (inputField && !stepInput) return;

    setIsLoading(true);

    const payload = inputField ? { [inputField]: stepInput } : undefined;

    authStepper(authStep, payload as any)
      .then(() => setStepInput(""))
      .finally(() => setIsLoading(false));
  }, [authStep, stepInput]);

  const submitStep = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      doSubmit();
    },
    [doSubmit]
  );

  const resetState = useCallback(() => {
    setStepInput("");
  }, []);

  const onClose = useCallback(() => {
    setAuthStep(null);
    setIsModalOpen(false);
  }, [setAuthStep]);

  const config = authStep ? stepperConfig[authStep] : null;

  const authQrUrl = useMemo<string | null>(() => {
    if (authSecret && email) {
      return buildOtpAuthUrl(authSecret, email);
    }
    return null;
  }, [authSecret, email]);

  const resendCode = useCallback(async () => {
    setIsLoading(true);
    await authStepper("OTP_RESEND", undefined).then(() => setTimer(60));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (authStep && config && !config.payload) {
      doSubmit();
    }
  }, [authStep, config, doSubmit]);

  return (
    <>
      {isStatic ? (
        <ModalContent>
          {config && (
            <>
              <ModalHeading $centered>{t(config.title)}</ModalHeading>
              <InnerContentForm onSubmit={(e) => submitStep(e)}>
                {config.inputField && (
                  <InputContainer>
                    {config.inputLabel && (
                      <AppText>{t(config.inputLabel)}</AppText>
                    )}

                    <TextInput
                      value={stepInput}
                      onChange={({ target: { value } }) => setStepInput(value)}
                      autoComplete="off"
                      name="step-field"
                    />
                  </InputContainer>
                )}

                {config.inputField && config.inputField === "email" && (
                  <>
                    {timerCount > 0 && (
                      <AppText
                        fontWeight={400}
                        style={{ marginBottom: "1.6rem" }}
                      >
                        {t("RESEND_IN")}{" "}
                        <AppText
                          isSpan
                          fontWeight={600}
                          style={{ color: colors.primary_600 }}
                        >
                          {timerCount}
                        </AppText>
                      </AppText>
                    )}
                    <MainButton
                      variant="contained"
                      fullWidth
                      fontSize={16}
                      style={{ marginBottom: "1.6rem" }}
                      onClick={() => resendCode()}
                      disabled={timerCount > 0 || isLoading}
                    >
                      {t("RESEND_EMAIL")}
                    </MainButton>
                  </>
                )}

                <ModalButton type="submit">{t("SUBMIT")}</ModalButton>
              </InnerContentForm>
            </>
          )}
        </ModalContent>
      ) : (
        <BasicModal
          isOpen={isModalOpen}
          onExited={resetState}
          onClose={onClose}
          showLoader={isLoading}
        >
          <ModalContent>
            <CloseModalButton onClose={onClose} label="close" />
            {config && (
              <>
                <ModalHeading $centered>{t(config.title)}</ModalHeading>
                <InnerContentForm onSubmit={(e) => submitStep(e)}>
                  <TwoFaQRContainer>
                    {authQrUrl && (
                      <QrContainer $noBorder>
                        <AppText style={{ margin: "0 0 1rem" }}>
                          {t("SCAN_QR_BY_AUTH_APP")}
                        </AppText>
                        <QrZone $isLarge>
                          <QRCode value={authQrUrl} size={170} />
                        </QrZone>
                      </QrContainer>
                    )}
                    {authSecret && (
                      <>
                        <AppText style={{ margin: "1rem 0" }}>
                          {t("MANUALY_ENTER_TO_AUTH_APP")}
                        </AppText>
                        <ReadInfo text={authSecret} withCopy />
                      </>
                    )}
                  </TwoFaQRContainer>
                  {config.inputField && (
                    <InputContainer $marginTop>
                      {config.inputLabel && (
                        <AppText>{t(config.inputLabel)}</AppText>
                      )}

                      <TextInput
                        value={stepInput}
                        onChange={({ target: { value } }) =>
                          setStepInput(value)
                        }
                        autoComplete="off"
                        name="step-field"
                      />
                    </InputContainer>
                  )}

                  {config.inputField && config.inputField === "email" && (
                    <>
                      {timerCount > 0 && (
                        <AppText
                          fontWeight={400}
                          style={{ marginBottom: "1.6rem" }}
                        >
                          {t("RESEND_IN")}{" "}
                          <AppText
                            isSpan
                            fontWeight={600}
                            style={{ color: colors.primary_600 }}
                          >
                            {timerCount}
                          </AppText>
                        </AppText>
                      )}
                      <MainButton
                        variant="contained"
                        fullWidth
                        fontSize={16}
                        style={{ marginBottom: "1.6rem" }}
                        onClick={() => resendCode()}
                        disabled={timerCount > 0 || isLoading}
                      >
                        {t("RESEND_CODE")}
                      </MainButton>
                    </>
                  )}

                  <ModalButton type="submit">{t("SUBMIT")}</ModalButton>
                </InnerContentForm>
              </>
            )}
          </ModalContent>
        </BasicModal>
      )}
    </>
  );
};

export default observer(AuthStepsModal);
